import type {MenuProps} from "antd";

import {DoubleLeftOutlined} from '@ant-design/icons';
import {CustomMenu} from "#src/components/Menu/CustomMenu";
import {Scrollbar} from "#src/components/scrollbar";
import {usePreferences} from "#src/hooks/use-preferences";
import {useLayout} from "#src/layout/hooks/use-layout";
import {cn} from "#src/utils/cn";
import {ConfigProvider, Menu, Popover, theme} from "antd";
import React, {useEffect, useRef, useState} from 'react';

import type {MenuItemType} from "./../layout-menu/types";

import './CustomSidebar.scss';

// import { IconReader } from "#src/components/icon-reader/IconReader";

interface CustomSidebarProps {
	items: MenuItemType[]
	/**
	 * Callback function triggered when a menu item is selected.
	 * Used for navigation and page switching.
	 */
	handleMenuSelect?: (key: string, mode: MenuProps["mode"]) => void
}

const emptyArray: MenuItemType[] = [];

/** Viewport cap for rail hover menu (matches prior SCSS intent); applied inline so it wins over CSS-in-JS. */
const RAIL_SUBMENU_MAX_HEIGHT = "calc(100dvh - 96px)";

export const CustomSidebar: React.FC<CustomSidebarProps> = ({
																															items = emptyArray,
																															handleMenuSelect,
																														}) => {
	const [activeKey, setActiveKey] = useState<string>('');
	const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
	const {isDark, sidebarTheme} = usePreferences();
	const {isTopNav, isMixedNav} = useLayout();
	const {token} = theme.useToken();

	/** Same as LayoutHeader so portaled Menu / Popover overlays match horizontal bar tokens. */
	const isFixedDarkTheme = isDark || (sidebarTheme === "dark" && (isMixedNav || isTopNav));

	const sidebarRailRef = useRef<HTMLElement | null>(null);
	const subMenuPanelRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!isPanelOpen) {
			return;
		}
		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as Node | null;
			if (!target) {
				return;
			}
			if (sidebarRailRef.current?.contains(target)) {
				return;
			}
			if (subMenuPanelRef.current?.contains(target)) {
				return;
			}
			setIsPanelOpen(false);
		};
		document.addEventListener('pointerdown', onPointerDown);
		return () => document.removeEventListener('pointerdown', onPointerDown);
	}, [isPanelOpen]);

	/**
	 * Handles the logic when a main sidebar icon is clicked.
	 * If the item has children, it toggles the persistent side panel.
	 * If it's a leaf node, it triggers navigation.
	 */
	const handleItemClick = (item: MenuItemType) => {
		if (activeKey === item.key) {
			// Toggle panel visibility if clicking the same active item
			setIsPanelOpen(!isPanelOpen);
		} else {
			setActiveKey(item.key as string);
			if (item.children && item.children.length > 0) {
				// Open persistent panel for items with sub-menus
				setIsPanelOpen(true);
			} else {
				// Direct navigation for top-level items without children
				setIsPanelOpen(false);
				handleMenuSelect?.(item.key as string, "inline");
			}
		}
	};

	const activeItem = items.find(i => i.key === activeKey);

	const railRow = (item: MenuItemType) => (
		<div className="relative w-full px-2">
			{/* Sidebar Item Shell */}
			<div
				className={cn(
					'menuItem',
					activeKey === item.key && isPanelOpen && 'active',
				)}
				onClick={() => handleItemClick(item)}
			>
				<div className="iconWrapper">
					<span className="text-[24px] text-white ">
						{item.icon}
					</span>
				</div>

				<span className="title text-white ">
					{item.label}
				</span>

				{/* Visual Arrow indicator for active/expanded state */}
				{activeKey === item.key && isPanelOpen && (
					<div className="activeArrow"/>
				)}
			</div>
		</div>
	);

	const railHoverMenu = (item: MenuItemType) => (
		<Menu
			mode="vertical"
			inlineIndent={16}
			theme={isDark ? "dark" : sidebarTheme}
			items={item.children as MenuProps["items"]}
			className={cn("!border-none min-w-0 min-h-0", "min-w-[240px]")}
			style={{
				minWidth: 240,
				minHeight: 0,
				maxHeight: RAIL_SUBMENU_MAX_HEIGHT,
				overflowX: "hidden",
				overflowY: "auto",
			}}
			onClick={({key}) => handleMenuSelect?.(key, "vertical")}
		/>
	);

	return (
		<>
			<ConfigProvider
				theme={{
					algorithm: isFixedDarkTheme
						? theme.darkAlgorithm
						: theme.defaultAlgorithm,
				}}
			>
				{/* Main Sidebar Container (Icon Bar) */}
				<aside
					ref={sidebarRailRef}
					className={cn(
					'sidebarContainer',
					`${!isDark ? "dynamic-gradient" : "bg-glass-2"}`,
				)}
					style={{
								 backgroundColor: token.colorPrimary,
							 }}
				>
					<Scrollbar className="w-full h-full sidebarRailScrollbar">
						<div className="py-4 flex flex-col items-center relative">
							{items.map((item) => {
								const hasSubmenu = Boolean(item.children && item.children.length > 0);
								if (hasSubmenu && !isPanelOpen) {
									return (
											<Popover
												key={item.key}
												trigger="hover"
												mouseEnterDelay={0.15}
												mouseLeaveDelay={0.1}
												placement="right"
												arrow={false}
												destroyOnHidden
												// classNames={{root: "sidebarRailSubmenuPopover"}}
												styles={{
													root: {
														overflow: "visible",
													},
													container: {
														padding: 0,
														minHeight: 0,
														overflow: "hidden",
													},
													content: {
														paddingInline: 0,
														paddingTop: token.paddingXXS,
														paddingBottom: token.paddingXXS,
														minHeight: 0,
														overflow: "visible",
													},
												}}
												content={(
<Scrollbar key="scbar">
{railHoverMenu(item)}
</Scrollbar>
)}
											>
												{railRow(item)}
											</Popover>
									);
								}

								return (
									<div key={item.key}>
										{railRow(item)}
									</div>
								);
							})}
						</div>
					</Scrollbar>
				</aside>
			</ConfigProvider>

			{/* 2. Persistent Click Panel (Secondary Menu):
				Triggered when a main item with children is clicked.
			*/}
			{isPanelOpen && activeItem?.children && (
				<div ref={subMenuPanelRef} className="subMenuPanel">
					{/* Header with Title and Close button */}
					<div className="panelHeader">
						<span className="panelTitle">
							{activeItem.label}
						</span>
						{/* <IconReader name='arrow-previous' onClick={() => setIsPanelOpen(false)}/> */}
						<DoubleLeftOutlined
							className="closeIcon"
							onClick={() => setIsPanelOpen(false)}
						/>
					</div>

					{/* Sub-menu list inside the persistent panel */}
					<div className="panelContent">
						<CustomMenu
							mode="inline"
							theme={isDark ? "dark" : "light"}
							items={activeItem.children as MenuProps["items"]}
							onClick={({key}) => {
								setIsPanelOpen(false);
								handleMenuSelect?.(key, "inline");
							}}
							className="border-none"
						/>
					</div>
				</div>
			)}
		</>
	);
};
